// Runtime error-code i18n surface.
//
// Locale catalogs are registered by the app's loaded locale entry. The legacy
// all-locale entrypoint registers all catalogs for apps that still require
// eager registration; current-language consumers register only one locale.
import type { errorCodeCommon as zhErrorCodeCommon } from "./i18n/features/zh/error-code-common";
import {
  type AppLocale,
  DEFAULT_LOCALE,
  normalizeLocale,
  getLocale as sharedGetLocale,
  setLocale as sharedSetLocale,
  subscribeLocaleChange as sharedSubscribeLocaleChange,
} from "./i18n/locale-state";
import { createI18nRuntime, type MessageTree } from "./i18n/runtime";

export type { AppLocale } from "./i18n/locale-state";
export {
  APP_LOCALE_LABELS,
  APP_LOCALES,
  DEFAULT_LOCALE,
  normalizeLocale,
} from "./i18n/locale-state";

/** Error-code dictionaries owned by one app transport. */
export type ErrorCodeDomainTree = {
  readonly apiCodes: object;
  readonly apiCodeResources: object;
  readonly apiCodeReasons: object;
};

export type ErrorCodeDomainCatalogs = {
  readonly "zh-CN": ErrorCodeDomainTree;
  readonly en: ErrorCodeDomainTree;
  readonly ja: ErrorCodeDomainTree;
};

export type ErrorCodeMessages = typeof zhErrorCodeCommon;

const LOCALES = ["zh-CN", "en", "ja"] as const;

const baseLocaleCatalogs: Partial<Record<AppLocale, MessageTree>> = {};

const attachedDomains: Record<AppLocale, ErrorCodeDomainTree[]> = {
  "zh-CN": [],
  en: [],
  ja: [],
};
const attachedDomainLocales = new Map<string, Set<AppLocale>>();

function composeCatalogs(locale: AppLocale): MessageTree {
  const base = baseLocaleCatalogs[locale] ?? {};
  if (attachedDomains[locale].length === 0) {
    return base;
  }
  const commonTree = base.common as MessageTree | undefined;
  const errorsTree = commonTree?.errors as MessageTree | undefined;
  return {
    ...base,
    common: {
      ...commonTree,
      errors: {
        ...errorsTree,
        apiCodes: Object.assign(
          {},
          errorsTree?.apiCodes as MessageTree | undefined,
          ...attachedDomains[locale].map((domain) => domain.apiCodes),
        ),
        apiCodeResources: Object.assign(
          {},
          errorsTree?.apiCodeResources as MessageTree | undefined,
          ...attachedDomains[locale].map((domain) => domain.apiCodeResources),
        ),
        apiCodeReasons: Object.assign(
          {},
          errorsTree?.apiCodeReasons as MessageTree | undefined,
          ...attachedDomains[locale].map((domain) => domain.apiCodeReasons),
        ),
      },
    },
  };
}

const localeCatalogs = Object.fromEntries(
  LOCALES.map((locale) => [locale, composeCatalogs(locale)]),
) as Record<AppLocale, MessageTree>;
const activeRuntime = createI18nRuntime({
  defaultLocale: DEFAULT_LOCALE,
  localeCatalogs,
  normalizeLocale,
  localeState: {
    getLocale: sharedGetLocale,
    setLocale: sharedSetLocale,
    subscribeLocaleChange: sharedSubscribeLocaleChange,
  },
});

function refreshLocaleCatalog(locale: AppLocale) {
  localeCatalogs[locale] = composeCatalogs(locale);
}

/** Registers the common error-code catalog owned by one loaded locale chunk. */
export function registerErrorCodeCommonLocale(locale: AppLocale, catalog: MessageTree) {
  if (baseLocaleCatalogs[locale] === catalog) {
    return;
  }
  baseLocaleCatalogs[locale] = catalog;
  refreshLocaleCatalog(locale);
}

/** Attaches the error-code domain owned by the current app. */
export function attachErrorCodeDomain(domainName: string, domains: ErrorCodeDomainCatalogs) {
  for (const locale of LOCALES) {
    attachErrorCodeDomainLocale(domainName, locale, domains[locale]);
  }
}

export function attachErrorCodeDomainLocale(
  domainName: string,
  locale: AppLocale,
  domain: ErrorCodeDomainTree,
) {
  const locales = attachedDomainLocales.get(domainName) ?? new Set<AppLocale>();
  if (locales.has(locale)) {
    return;
  }
  locales.add(locale);
  attachedDomainLocales.set(domainName, locales);
  attachedDomains[locale].push(domain);
  refreshLocaleCatalog(locale);
}

export function listAttachedErrorCodeDomains(): readonly string[] {
  return [...attachedDomainLocales.keys()];
}

export const setLocale = (locale: string) => sharedSetLocale(locale);
export const getLocale = () => activeRuntime.getLocale();
export const subscribeLocaleChange = (listener: (locale: AppLocale) => void) =>
  activeRuntime.subscribeLocaleChange(listener);
export const createMessageProxy = <T extends object>(basePath: string) =>
  activeRuntime.createMessageProxy<T>(basePath);
export const hasMessage = (path: string, locale?: string) => activeRuntime.hasMessage(path, locale);
export const t = (
  path: string,
  params?: Record<string, string | number | boolean | null | undefined>,
  locale?: string,
) => activeRuntime.t(path, params, locale);
export const formatMessage = activeRuntime.formatMessage;
export const formatEnumPath = activeRuntime.formatEnumPath;
export const formatEnumLabel = activeRuntime.formatEnumLabel;
export const formatBooleanLabel = activeRuntime.formatBooleanLabel;
export const formatLocalizedList = activeRuntime.formatLocalizedList;
export const formatActionObjectLabel = activeRuntime.formatActionObjectLabel;
export const formatActionObjectMessage = activeRuntime.formatActionObjectMessage;
export const formatActionObjectIntlLabel = activeRuntime.formatActionObjectIntlLabel;

export function getMessageCatalogs(): Record<AppLocale, MessageTree> {
  return localeCatalogs;
}

export type {
  DeepPartialMessageTree,
  FormatMessageLike,
  MessageDescriptor,
  MessageParams,
} from "./i18n/runtime";
